import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PLANS, type Caps, type Meter, type Plan, type Usage } from "@backstory/protocol";
import { ErrorState, Skeleton } from "../../components/States";
import { Field, Meter as MeterBar, Switch, fmtNum, fmtUsd, rateLabel, singular, useToast } from "../../components/ui";
import { SaveBar, settingsEqual } from "../../components/UnsavedChanges";
import { billingApi, billingKeys } from "./api";
import { LimitsBanner, meterLimitStatus, meterStopped, useOrgLimits } from "./limits";

const BILLING_METERS = ["stored_sessions", "custom_events", "canvas_minutes", "live_minutes"] as const satisfies readonly Meter[];
// Shown with the meters above, but not given a hard cap here: AI pauses at zero
// instead of billing past the package, and backend data has its own daily and
// monthly limits in Backend settings.
const EXTRA_METERS = ["backend_gb", "deep_analyses", "ai_credits_usd"] as const satisfies readonly Meter[];
const BAR_METERS = [...BILLING_METERS, ...EXTRA_METERS];
const METER_LABELS: Record<(typeof BAR_METERS)[number], { label: string; unit: string; per1000?: boolean; perMillion?: boolean; usd?: boolean }> = {
  // Sessions are the one meter the plans price by the thousand.
  stored_sessions: { label: "Stored sessions", unit: "sessions", per1000: true },
  custom_events: { label: "Custom events", unit: "events", perMillion: true },
  canvas_minutes: { label: "Visual recording minutes", unit: "min" },
  live_minutes: { label: "Live Assist minutes", unit: "participant-min" },
  backend_gb: { label: "Backend data", unit: "GB" },
  deep_analyses: { label: "Deep analyses", unit: "analyses" },
  ai_credits_usd: { label: "AI credits", unit: "", usd: true },
};

const EMPTY_CAPS: Record<Meter, string> = { stored_sessions: "", custom_events: "", backend_gb: "", ai_credits_usd: "", deep_analyses: "", canvas_minutes: "", live_minutes: "", mcp_calls: "", digests: "", projects: "" };

/**
 * Caps move in thousands. Packages are sold in thousands and sessions are
 * priced by the thousand, so a cap of 10,437 is precision nobody chose and no
 * invoice reflects.
 */
export const CAP_STEP = 1000;

function connectorIncluded(plan: string): boolean {
  const features = PLANS[plan as Plan]?.features ?? [];
  return features.includes("mcp") || features.includes("everything");
}

/**
 * A cap is a whole number of units, so the field only ever holds digits. Typed
 * and pasted junk is dropped at the keystroke rather than accepted and silently
 * discarded on save, which is how a cap the operator believed they set ends up
 * never having existed.
 */
export function sanitizeCap(raw: string): string {
  return raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 9);
}

/**
 * Snaps a typed cap onto the step, with the included package as the floor:
 * capping below what the plan already paid for throws the package away rather
 * than controlling any spend.
 */
export function snapCap(raw: string, included: number): string {
  if (raw === "") return "";
  const floor = included > 0 ? included : CAP_STEP;
  return String(Math.max(floor, Math.round(Number(raw) / CAP_STEP) * CAP_STEP));
}

/** Caps form helper shared by both deployment-specific billing pages. */
export function capsFromForm(form: Record<Meter, string>, onSessionCap: Caps["onSessionCap"], overage: boolean, spendCap: string, customEvents = false): Caps {
  const hardCap: Partial<Record<Meter, number>> = {};
  for (const m of Object.keys(form) as Meter[]) {
    const v = form[m].trim();
    if (v !== "" && !Number.isNaN(Number(v)) && Number(v) >= 0) hardCap[m] = Number(v);
  }
  const caps: Caps = { hardCap, onSessionCap, deepAnalysisOverageAllowed: overage, customEventOverageAllowed: customEvents };
  if (spendCap.trim() !== "" && !Number.isNaN(Number(spendCap))) caps.deepAnalysisSpendCapUsd = Number(spendCap);
  return caps;
}

type MeterFacts = { used: number; included: number; unit: string; price?: number; per1000?: boolean; perMillion?: boolean };

/** What the overage between the package and the cap is worth. */
function overageCost(units: number, price: number, per1000?: boolean, perMillion?: boolean): number {
  if (perMillion) return (units / 1_000_000) * price;
  return per1000 ? (units / 1000) * price : units * price;
}

/**
 * What a cap will do right now. A cap is a spending decision, so it is stated
 * in the money it allows rather than in units the reader has to price
 * themselves.
 */
function capStatus(value: string, f: MeterFacts): { tone: "hint" | "warn" | "error"; text: string } {
  if (value === "") {
    return f.price != null
      ? { tone: "hint", text: `Unlimited — every extra ${f.perMillion ? "million" : f.per1000 ? "1,000" : singular(f.unit)} is billed` }
      : { tone: "hint", text: "Unlimited" };
  }
  const n = Number(value);
  if (n <= f.used) return { tone: "error", text: `Already past — usage is ${fmtNum(f.used)}` };
  if (f.included > 0 && n < f.included) return { tone: "warn", text: `Below the ${fmtNum(f.included)} included` };
  if (n === f.included) return { tone: "hint", text: "Stops at the package — no overage" };
  const extra = n - f.included;
  return f.price != null
    ? { tone: "hint", text: `Up to ${fmtUsd(overageCost(extra, f.price, f.per1000, f.perMillion))} of overage` }
    : { tone: "hint", text: `${fmtNum(extra)} ${f.unit} past the package` };
}

/**
 * A stepper rather than a free number field: the first press lands on the
 * included package — the cap most people actually want, "spend nothing extra" —
 * and every press after that buys one more thousand.
 */
function CapControl({ meter, label, value, onChange, facts }: { meter: string; label: string; value: string; onChange: (v: string) => void; facts: MeterFacts }) {
  const [focused, setFocused] = useState(false);
  const floor = facts.included > 0 ? facts.included : CAP_STEP;
  const n = value === "" ? null : Number(value);
  const bump = (dir: 1 | -1) => {
    if (n == null) {
      if (dir > 0) onChange(String(floor));
      return;
    }
    const next = Math.round(n / CAP_STEP) * CAP_STEP + dir * CAP_STEP;
    onChange(next < floor ? "" : String(next));
  };
  const status = capStatus(value, facts);
  return (
    <div className="cap-cell">
      {/* Both steps sit left of the field so the digits land on the column's
          right edge, under the heading and the status line, and so repeat
          presses do not travel across the number. */}
      <div className="cap-control">
        <span className="cap-steps">
          <button type="button" className="btn sm cap-step" onClick={() => bump(-1)} disabled={n == null} aria-label={`Lower the ${label} cap`}>
            −
          </button>
          <button type="button" className="btn sm cap-step" onClick={() => bump(1)} aria-label={`Raise the ${label} cap`}>
            +
          </button>
        </span>
        {/* Grouped while idle, plain digits while typing: thousands separators
            appearing under the caret fight whoever is editing the number. */}
        <input
          className="input cap-input"
          aria-label={`${label} hard cap`}
          aria-describedby={`cap-${meter}-status`}
          aria-invalid={status.tone === "error" || undefined}
          inputMode="numeric"
          autoComplete="off"
          placeholder="No cap"
          value={focused || n == null ? value : fmtNum(n)}
          onChange={(e) => onChange(sanitizeCap(e.target.value))}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onChange(snapCap(value, facts.included));
          }}
        />
      </div>
      <span className={`cap-status ${status.tone}`} id={`cap-${meter}-status`}>{status.text}</span>
    </div>
  );
}

export function BillingUsage({ orgId, fixedCaps = false }: { orgId: string; fixedCaps?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const usage = useQuery({ queryKey: billingKeys.usage(orgId), queryFn: () => billingApi.usage(orgId), retry: 0 });
  const limits = useOrgLimits(orgId);
  const [capsForm, setCapsForm] = useState<Record<Meter, string>>({ ...EMPTY_CAPS });
  const [onSessionCap, setOnSessionCap] = useState<Caps["onSessionCap"]>("triggers_only");
  const [eventOverage, setEventOverage] = useState(false);
  const [savedCapsForm, setSavedCapsForm] = useState<Record<Meter, string>>({ ...EMPTY_CAPS });
  const [savedSessionCap, setSavedSessionCap] = useState<Caps["onSessionCap"]>("triggers_only");
  const [savedEventOverage, setSavedEventOverage] = useState(false);

  useEffect(() => {
    const c = usage.data?.caps;
    if (!c) return;
    const next = {
      stored_sessions: c.hardCap.stored_sessions?.toString() ?? "",
      custom_events: c.hardCap.custom_events?.toString() ?? "",
      backend_gb: c.hardCap.backend_gb?.toString() ?? "",
      ai_credits_usd: c.hardCap.ai_credits_usd?.toString() ?? "",
      deep_analyses: c.hardCap.deep_analyses?.toString() ?? "",
      canvas_minutes: c.hardCap.canvas_minutes?.toString() ?? "",
      live_minutes: c.hardCap.live_minutes?.toString() ?? "",
      mcp_calls: c.hardCap.mcp_calls?.toString() ?? "",
      digests: c.hardCap.digests?.toString() ?? "",
      projects: c.hardCap.projects?.toString() ?? "",
    };
    setCapsForm(next);
    setSavedCapsForm(next);
    setOnSessionCap(c.onSessionCap);
    setSavedSessionCap(c.onSessionCap);
    setEventOverage(!!c.customEventOverageAllowed);
    setSavedEventOverage(!!c.customEventOverageAllowed);
  }, [usage.data?.caps]);
  const dirty = !fixedCaps && (!settingsEqual(capsForm, savedCapsForm) || onSessionCap !== savedSessionCap || eventOverage !== savedEventOverage);
  const discardCaps = () => {
    setCapsForm(savedCapsForm);
    setOnSessionCap(savedSessionCap);
    setEventOverage(savedEventOverage);
  };

  const saveCaps = useMutation({
    mutationFn: () => {
      // Snap again here: saving from a field still being typed in would
      // otherwise persist the one value the stepper cannot reach.
      const snapped = { ...capsForm };
      for (const m of BILLING_METERS) snapped[m] = snapCap(capsForm[m], usage.data?.meters[m]?.included ?? 0);
      setCapsForm(snapped);
      return billingApi.patchCaps(orgId, capsFromForm(snapped, onSessionCap, usage.data?.caps.deepAnalysisOverageAllowed ?? false, usage.data?.caps.deepAnalysisSpendCapUsd?.toString() ?? "", eventOverage));
    },
    onSuccess: () => {
      const snapped = { ...capsForm };
      for (const m of BILLING_METERS) snapped[m] = snapCap(capsForm[m], usage.data?.meters[m]?.included ?? 0);
      setCapsForm(snapped);
      setSavedCapsForm(snapped);
      setSavedSessionCap(onSessionCap);
      setSavedEventOverage(eventOverage);
      toast.push("info", "Caps saved.");
      void qc.invalidateQueries({ queryKey: billingKeys.usage(orgId) });
      void qc.invalidateQueries({ queryKey: billingKeys.limits(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const totalOverageUsd = useMemo(
    () => BILLING_METERS.reduce((sum, m) => sum + (usage.data?.meters[m]?.overageUsd ?? 0), 0),
    [usage.data],
  );

  return (
    <>
    <div className="card section" style={{ marginTop: 16 }}>
      <div className="section-head" style={{ padding: 0 }}>
        <h2>Usage this month</h2>
        {totalOverageUsd > 0 && (
          <span className="badge danger" data-testid="overage-total">
            {fmtUsd(totalOverageUsd)} overage so far
          </span>
        )}
      </div>
      {!fixedCaps && <p className="muted small">Extra usage is invoiced monthly and due in 7 days.</p>}
      {usage.isPending ? (
        <Skeleton lines={3} />
      ) : usage.isError ? (
        <ErrorState error={usage.error} retry={() => void usage.refetch()} />
      ) : (
        <>
          <LimitsBanner orgId={orgId} limits={limits} />
          <div className="meters">
            {BAR_METERS.map((m) => {
              const v = usage.data.meters[m];
              if (!v) return null;
              return (
                <MeterBar
                  key={m}
                  label={METER_LABELS[m].label}
                  used={v.used}
                  included={v.included}
                  projected={meterStopped(m, limits) ? undefined : v.projectedMonthEnd}
                  unit={METER_LABELS[m].unit}
                  format={METER_LABELS[m].usd ? fmtUsd : undefined}
                  overageUnits={v.overageUnits}
                  overageUsd={fixedCaps ? 0 : v.overageUsd}
                  overageUnitPrice={fixedCaps || METER_LABELS[m].usd ? undefined : v.overageUnitPrice}
                  overagePer1000={METER_LABELS[m].per1000}
                  overagePerMillion={METER_LABELS[m].perMillion}
                  status={meterLimitStatus(m, limits)}
                />
              );
            })}
          </div>
          <p className="muted small" style={{ marginTop: 12 }} data-testid="connector-calls">
            Connector calls this month: <strong>{fmtNum(usage.data.connectorCalls ?? 0)}</strong>
          </p>
          {!connectorIncluded(usage.data.plan) && (
            <p className="muted small" data-testid="connector-credits">
              Call credits remaining: <strong>{fmtNum(usage.data.connectorCallCredits ?? 0)}</strong>. Each connector call spends one.
            </p>
          )}
          <CustomEventBreakdown report={usage.data.customEvents} />
          {fixedCaps ? (
            <p className="muted small" style={{ marginTop: 16 }}>This billing provider stops at the included package limits; usage overages are not charged.</p>
          ) : (
            <>
              <h3 style={{ marginTop: 20 }}>Caps and overage</h3>
              <p className="muted small">Caps move in {fmtNum(CAP_STEP)}s, and the lowest one is the included package itself — that cap spends nothing past the plan.</p>
              <div className="table-scroll">
                <table className="table caps-table">
                  <caption className="sr-only">Included packages, overage rates, and hard caps</caption>
                  <thead>
                    <tr>
                      <th scope="col">Meter</th>
                      <th scope="col">Included</th>
                      <th scope="col">Past the package</th>
                      <th scope="col">Hard cap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BILLING_METERS.map((m) => {
                      const v = usage.data.meters[m];
                      if (!v) return null;
                      const { label, unit, per1000, perMillion } = METER_LABELS[m];
                      const price = v.overageUnitPrice;
                      // Nothing included and no price to go past it means the
                      // plan does not carry the meter at all, so there is no
                      // spending for a cap to control.
                      const offPlan = v.included <= 0 && price == null;
                      return (
                        <tr key={m} className="static">
                          <th scope="row">{label}</th>
                          <td className="num">{v.included > 0 ? `${fmtNum(v.included)} ${unit}` : <span className="muted">none</span>}</td>
                          <td className="muted">{offPlan ? `Not on the ${usage.data.plan} plan` : price != null ? rateLabel(price, unit, per1000, perMillion) : "not billed"}</td>
                          <td>
                            {offPlan ? (
                              <span className="muted">—</span>
                            ) : (
                              <CapControl
                                meter={m}
                                label={label}
                                value={capsForm[m]}
                                onChange={(next) => setCapsForm({ ...capsForm, [m]: next })}
                                facts={{ used: v.used, included: v.included, unit, price, per1000, perMillion }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ maxWidth: 420, marginTop: 12 }}>
                <Field label="When the session cap is reached" hint="Applies to the stored sessions cap.">
                  {(p) => (
                    <select {...p} className="input" value={onSessionCap} onChange={(e) => setOnSessionCap(e.target.value as Caps["onSessionCap"])}>
                      <option value="triggers_only">Keep recording sessions with errors and triggers only</option>
                      <option value="stop">Stop recording</option>
                    </select>
                  )}
                </Field>
              </div>
              {(() => {
                const plan = PLANS[usage.data.plan as Plan];
                const eventPrice = plan?.customEventOveragePerMillionUsd;
                return (
                  <div style={{ marginTop: 12 }}>
                    <Switch
                      checked={eventOverage}
                      disabled={eventPrice == null}
                      onChange={setEventOverage}
                      label="Charge for custom events past the included amount"
                      hint={eventPrice == null ? "This plan stops when the included events are used." : `Off stops custom events at the included amount. On charges ${fmtUsd(eventPrice)} per extra million.`}
                    />
                  </div>
                );
              })()}
              <p className="muted small">AI never creates postpaid overage. It pauses at zero until prepaid credits are added.</p>
            </>
          )}
        </>
      )}
    </div>
    {!fixedCaps && (
      <SaveBar
        dirty={dirty}
        saving={saveCaps.isPending}
        onSave={() => saveCaps.mutate()}
        onDiscard={discardCaps}
        label="usage caps"
      />
    )}
    </>
  );
}

function CustomEventBreakdown({ report }: { report: Usage["customEvents"] }) {
  if (!report || (report.projects.length === 0 && report.names.length === 0)) return null;
  return (
    <div className="form-grid" style={{ marginTop: 16 }}>
      <div>
        <h3>By key</h3>
        <ul>
          {report.projects.map((p) => (
            <li key={p.projectId}>
              {p.name || "Untitled"} · {fmtNum(p.used)}
              {p.cap ? ` of ${fmtNum(p.cap)}` : ""}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3>By name</h3>
        <ul>
          {report.names.map((n) => (
            <li key={n.name}>{n.name} · {fmtNum(n.used)}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
